import React from 'react'
import log from 'loglevel'
import styled from 'styled-components/macro'
import palette from 'src/designSystem/variables/palette'
import { LinkIcon } from 'src/components/Icons/FileType'
import { openInNewTab } from 'src/Utils/globalHelpers'
import { getReceptorTemplate } from 'src/xgenerated'
import {
  Tooltip,
  TooltipOverlayType,
  TooltipPositions,
} from 'src/components/Reusable/Tooltip/Tooltip'
import { TextButton } from '../Buttons/TextButton'

export const ViewInSourceButton = ({
  receptorModelId,
  link,
  shortName,
  style,
}: {
  receptorModelId: string
  link: string
  shortName?: string
  style?: React.CSSProperties
}): JSX.Element => {
  let receptorName = 'Source'

  if (shortName) {
    receptorName = shortName
  } else {
    try {
      const receptorTemplate = getReceptorTemplate(receptorModelId)
      receptorName = receptorTemplate?.name || 'Source'
    } catch (e) {
      if (receptorModelId.startsWith('trr-')) {
        log.error('Error getting receptor template', e)
      } else {
        log.info(
          'Invalid receptor model id. Possibly a user uploaded document with the same caption as a linked document.',
          e,
        )
      }
    }
  }

  return (
    <TextButton
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        openInNewTab(link)
      }}
      style={style}
    >
      {`View in ${receptorName}`}
      <LinkIcon />
    </TextButton>
  )
}

// Which evidence types opt into the Details hover-table treatment, and which
// of their columns carries the JSON payload for it. Keyed by the evidence's
// own caption — the same string the receptor passes as `caption` to
// receptor_sdk.NewEvidence(...) — rather than matching any evidence's column
// literally named "Details", so an unrelated receptor's own "Details" column
// (plain text that happens to parse as {label,value}[] JSON) isn't
// accidentally rendered as a hover table too. Add an entry here whenever a
// receptor's evidence uses the DependabotAlertDetail-style JSON contract
// (see receptors/trr-github/github/dependabotalerts.go).
const DETAILS_COLUMN_FIELD_BY_EVIDENCE_CAPTION: Record<string, string> = {
  'GitHub Dependabot Vulnerability Alerts': 'Details',
}

export const getDetailsColumnField = (
  evidenceCaption?: string,
): string | undefined =>
  evidenceCaption
    ? DETAILS_COLUMN_FIELD_BY_EVIDENCE_CAPTION[evidenceCaption]
    : undefined

type DetailsRow = { label: string; value: string }

// Parses the JSON-array-of-{label,value} contract a receptor's "Details"
// column is expected to use (see DependabotAlertDetail in
// receptors/trr-github/github/dependabotalerts.go). Returns null for
// anything else so callers can fall back to plain-text rendering.
const parseDetailsRows = (raw?: string): DetailsRow[] | null => {
  if (!raw) {
    return null
  }
  try {
    const parsed: unknown = JSON.parse(raw)
    if (
      !Array.isArray(parsed) ||
      parsed.length === 0 ||
      !parsed.every(
        (row): row is DetailsRow =>
          typeof row === 'object' &&
          row !== null &&
          typeof (row as DetailsRow).label === 'string' &&
          typeof (row as DetailsRow).value === 'string',
      )
    ) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

const DetailsPreview = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  width: 100%;

  &:focus-visible {
    outline: 2px solid ${palette.blue['400']};
    outline-offset: 2px;
    border-radius: 4px;
  }
`

const DetailsTable = styled.table`
  border-collapse: collapse;
  font-size: 12px;

  th,
  td {
    text-align: left;
    padding: 2px 8px 2px 0;
    vertical-align: top;
  }

  th {
    font-weight: 600;
    white-space: nowrap;
    padding-right: 12px;
  }
`

// Renders an evidence grid cell whose value is a Details JSON payload as a
// short text preview with a real hover table of its label/value pairs. Falls
// back to plain text if the value isn't in the expected shape.
export const DetailsCell = ({ value }: { value?: string }): JSX.Element => {
  const rows = parseDetailsRows(value)
  if (!rows) {
    return <>{value}</>
  }
  const preview = rows.map((row) => `${row.label}: ${row.value}`).join(' | ')
  return (
    <Tooltip
      id="evidence-details-cell-tooltip"
      overlayType={TooltipOverlayType.popover}
      placement={TooltipPositions.top}
      tooltipBody={
        <DetailsTable>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <th>{row.label}</th>
                <td>{row.value}</td>
              </tr>
            ))}
          </tbody>
        </DetailsTable>
      }
    >
      {/* Focusable so the tooltip's built-in focus trigger reveals the full
          detail table for keyboard users — the preview text is ellipsized and
          there is no other way to reach the rest of it without a mouse. */}
      <DetailsPreview tabIndex={0}>{preview}</DetailsPreview>
    </Tooltip>
  )
}
